import mixpanel from "mixpanel-browser";
import { env } from "@/lib/env";
import type { AnalyticsEventName } from "./events";

/**
 * Product analytics wrapper (Mixpanel) — CHIEF-SCOPED.
 *
 * Goal: understand how fire CHIEFS — the paying customer — use the admin
 * dashboard, so we can (a) invest in the surfaces that deliver value and
 * (b) see which departments get enough value to support higher / expanded
 * pricing. Two hard rules:
 *
 *  1. **Opt-in by token.** With no `VITE_MIXPANEL_TOKEN` the whole module is a
 *     no-op — `init` never connects and every `track`/`identify` short-circuits.
 *     Local dev and CI therefore send nothing and behave identically.
 *
 *  2. **Chiefs only — never internal staff, never firefighter health/PII.**
 *     We identify and track ONLY when the signed-in user is a chief. RespAi's
 *     own `respai_admin` super-admins and every other role emit nothing, so our
 *     internal usage never pollutes customer-value data. We record the chief's
 *     own work email + role + an opaque department key and *usage* events
 *     (which feature, how often) — never a firefighter's identity, exposure /
 *     health readings, or raw resource UUIDs (paths normalize to templates).
 */

let enabled = false;
let initialized = false;
// True only while a CHIEF is the active identity. Gates every emission, so a
// non-chief session (respai_admin internal staff, or any other role) sends
// nothing at all — not even page views.
let trackingAllowed = false;

const CHIEF_ROLE = "chief";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapse identifying path segments (UUIDs, long numeric ids) to `:id` so the
 * `page_viewed` event groups by route template and never leaks a personnel or
 * incident id into analytics. `/personnel/3fa…` → `/personnel/:id`.
 */
export function normalizePath(pathname: string): string {
  const normalized = pathname
    .split("/")
    .map((seg) => (UUID_RE.test(seg) || /^\d+$/.test(seg) ? ":id" : seg))
    .join("/");
  return normalized.length > 0 ? normalized : "/";
}

/** Initialize analytics once. No-op (stays disabled) when no token is set. */
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;
  const token = env.mixpanelToken;
  if (!token) return;
  try {
    mixpanel.init(token, {
      debug: env.mixpanelDebug,
      // We own SPA navigation, so fire page views manually on route change
      // (auto-pageview would only see the first hard load).
      track_pageview: false,
      persistence: "localStorage",
      ...(env.mixpanelApiHost ? { api_host: env.mixpanelApiHost } : {}),
    });
    enabled = true;
  } catch {
    // A bad token / blocked SDK must never break the dashboard.
    enabled = false;
  }
}

export type AnalyticsIdentity = {
  sub: string;
  email: string;
  role: string | undefined;
  roles: string[];
  departmentId: string | undefined;
};

/**
 * Stable, opaque pseudonym for a tenant (department) id. We want to segment
 * usage by customer, but rule 2 forbids mirroring a raw internal resource UUID
 * into a third-party analytics store (the same UUID appears in URLs, the DB and
 * logs, so exporting it widens correlation/blast-radius). A deterministic
 * FNV-1a hash keeps cross-event grouping ("which department uses what") while
 * making the value non-reversible without the original id set. Undefined id →
 * undefined (no property sent).
 */
function tenantKey(departmentId: string | undefined): string | undefined {
  if (!departmentId) return undefined;
  let h = 0x811c9dc5;
  for (let i = 0; i < departmentId.length; i++) {
    h ^= departmentId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `dept_${(h >>> 0).toString(36)}`;
}

/**
 * Tie subsequent events to the signed-in CHIEF. A non-chief identity (notably a
 * `respai_admin` internal super-admin) leaves tracking OFF and is never
 * identified, so the analytics store only ever holds paying-customer behaviour.
 * `role` and an opaque, pseudonymized `department` key are registered as super
 * properties so every event is segmentable by them — without the raw tenant UUID.
 */
export function identifyUser(identity: AnalyticsIdentity): void {
  if (!enabled || !identity.sub) return;
  if (!identity.roles.includes(CHIEF_ROLE)) {
    // Not the paying customer (internal staff / non-chief): keep tracking off so
    // no events — including page views already armed elsewhere — are emitted.
    trackingAllowed = false;
    return;
  }
  trackingAllowed = true;
  try {
    const department = tenantKey(identity.departmentId);
    mixpanel.identify(identity.sub);
    mixpanel.people.set({
      // Chief's own work email — used to recognize a customer. Never a
      // firefighter's. Health/exposure data is never sent here.
      $email: identity.email || undefined,
      role: identity.role,
      roles: identity.roles,
      department,
    });
    mixpanel.register({
      role: identity.role,
      department,
    });
  } catch {
    // ignore — analytics is best-effort
  }
}

/**
 * Register account-level context so chief usage is sliceable for pricing: which
 * plan tiers engage most, whether bigger departments extract more value, and who
 * is outgrowing a pilot and ready to convert/upsell. These super properties ride
 * every subsequent event. No-op outside a tracked (chief) session, so it is safe
 * to call for any authenticated user.
 */
export function registerAccountContext(ctx: {
  plan?: string;
  firefighterCount?: number;
}): void {
  if (!enabled || !trackingAllowed) return;
  const props: Record<string, unknown> = {};
  if (ctx.plan) props.plan = ctx.plan;
  if (typeof ctx.firefighterCount === "number") {
    props.firefighter_count = ctx.firefighterCount;
  }
  if (Object.keys(props).length === 0) return;
  try {
    mixpanel.register(props);
    mixpanel.people.set(props);
  } catch {
    // ignore — analytics is best-effort
  }
}

export type TrackOptions = {
  /** `sendBeacon` survives page teardown — use it for pagehide-time events. */
  transport?: "xhr" | "sendBeacon";
};

/** Record a feature-usage event. No-op unless a chief session is active. */
export function trackEvent(
  event: AnalyticsEventName,
  properties?: Record<string, unknown>,
  options?: TrackOptions,
): void {
  if (!enabled || !trackingAllowed) return;
  try {
    if (options) {
      mixpanel.track(event, properties, options);
    } else {
      mixpanel.track(event, properties);
    }
  } catch {
    // ignore — analytics is best-effort
  }
}

/** Record a SPA page view. No-op unless a chief session is active. */
export function trackPageView(pathname: string): void {
  if (!enabled || !trackingAllowed) return;
  const path = normalizePath(pathname);
  try {
    mixpanel.track("page_viewed", { path });
  } catch {
    // ignore — analytics is best-effort
  }
}

/** Clear identity + disarm tracking on sign-out so the next user starts fresh. */
export function resetAnalytics(): void {
  trackingAllowed = false;
  if (!enabled) return;
  try {
    mixpanel.reset();
  } catch {
    // ignore — analytics is best-effort
  }
}
